import React, { memo } from 'react';
import PropTypes from 'prop-types';
import ShowMoreText from 'react-show-more-text';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import Avatar from '@components/@core/Avatar';
import Button from '@components/@core/AnimatedButton';
import Rating from '@components/@core/Rating';
import { AppActions, CompanyActions, VendorActions } from '~/actions';
import { convertUrl, hasHtml } from '~/services/util';

const CompanyItem = (props) => {
    const { type } = props;
    const data = { ...(props.data ?? {}), vrs: type === 'simple' ? (props.data?.vrs ?? {}) : (props.data ?? {}) };
    const name = data?.name;
    const description = data?.data?.profile?.about || data?.vrs?.data?.description || '';
    const url = convertUrl(data?.data?.profile?.avatar || '/images/default_company.svg');
    const cover = convertUrl(data?.data?.profile?.cover, '/images/company.png');
    const stats = data?.vrs?.stats ?? data?.vrs?.vrs?.stats;
    const productCount = stats?.product_count || 0;
    const recommend = stats?.rating_recommended_avg || '0';
    const ratingCount = stats?.rating_count || 0;
    const rating = stats?.rating_avg || '0';
    const phone = data?.data?.profile?.contact?.phone || data?.data?.profile?.cell_phone || '';
    const sponsoredLevel = data?.vrs?.data?.sponsored_level || 'wiki';
    const isSponsored = sponsoredLevel === 'gold' || sponsoredLevel === 'silver';

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const token = useSelector((state) => state.app.token);

    const handleOpenRate = (vr) => () => {
        if (token) {
            const after = {
                type: 'MODAL_REQUEST',
                payload: {
                    type: 'Rate Vendor',
                    data: { before: { company: vr.parent_id, category: null, product: null } },
                },
            };
            dispatch(VendorActions.getallvendorcompaniesRequest({ after }));
            dispatch(VendorActions.getcategoriesRequest({ isCategory: true }));
        } else {
            const route = location.pathname;
            dispatch(AppActions.modalRequest({ type: 'Authentication', data: { before: { route } } }));
        }
    };

    const handleOpenDetail = (vr) => () => {
        const businessId = vr?.vrs?.vrs?.business_id || vr?.business_id;
        const slug = vr?.vrs?.vrs?.entity?.slug || vr?.vrs?.vrs?.slug || vr?.entity?.slug || vr?.slug;
        if (businessId) {
            const payload = { id: businessId };
            if (slug) {
                window.location = `/${slug}/about`;
            } else {
                const route = slug ? `/${slug}/about` : `/business/about?id=${businessId}`;
                dispatch(CompanyActions.getbusinessRequest(payload, route));
            }
        } else {
            toast.error('This product does not have business yet. Please reach out to Brandon!', {
                position: toast.POSITION.TOP_RIGHT,
                pauseOnFocusLoss: false,
                hideProgressBar: true,
            });
        }
    };

    return (
        <div className={`vendor-search-companies-item ${type}`}>
            {type === 'simple' ? (
                <>
                    <div className="vendor-company-item-bg mb-3" style={{ backgroundImage: `url(${cover})` }} onClick={handleOpenDetail(data.vrs)}>
                        <Avatar name={name} url={url} type="logo" size="small" className="vendor-company-logo" borderColor="#969faa" isSponsored={isSponsored} />
                    </div>
                    <div className="vendor-company-detail">
                        <p className="dsl-b16 bold mb-2">{name}</p>
                        <div className="vendor-company-item-description">
                            <ShowMoreText lines={3} more="Read more" less="Read less">
                                {hasHtml(description) ? <div dangerouslySetInnerHTML={{ __html: description }} /> : <p className="dsl-b14">{description}</p>}
                            </ShowMoreText>
                        </div>
                        <div className="d-h-start mb-2">
                            <p className="dsl-b12 company-detail-label">Products</p>
                            <p className="dsl-b14 mb-0">{productCount}</p>
                        </div>
                        <div className="d-h-start mb-2">
                            <p className="dsl-b12 company-detail-label">Ave rating</p>
                            <div className="d-h-start">
                                <Rating score={Number(rating)} />
                            </div>
                        </div>
                        <div className="d-h-start mb-2">
                            <p className="dsl-b12 company-detail-label">% Recommended</p>
                            <p className="dsl-b14 mb-0">{Math.round(recommend * 100) / 100}%</p>
                        </div>
                    </div>
                </>
            ) : (
                <Row className="mx-0 cursor-pointer" onClick={handleOpenDetail(data)}>
                    <Col className="px-0 col-ssm-3" xs={2}>
                        <Avatar name={name} url={url} type="logo" size="small" isSponsored={isSponsored} />
                    </Col>
                    <Col className="px-0" xs={8}>
                        <p className="dsl-b16 bold mb-2">{name}</p>
                        <div className="d-h-start mb-2 d-sm-block">
                            <Rating score={rating} />
                            <p className="dsl-b12 ms-2 mb-0">{`${Math.round(recommend * 100) / 100}% Recommended  ${ratingCount} Ratings`}</p>
                        </div>
                        <div className="vendor-company-item-description mb-2">
                            {hasHtml(description) ? <div dangerouslySetInnerHTML={{ __html: description }} /> : <p className="dsl-b14">{description}</p>}
                        </div>
                    </Col>
                    <Col className="px-0 d-flex flex-column justify-content-between" xs={2}>
                        <div className="mb-3">
                            <p className="dsl-b14 text-end mb-2">{phone}</p>
                        </div>
                        <div className="d-flex flex-column align-items-end">
                            {!!data?.vrs?.parent_id && <Button type="link" name="RATE IT" onClick={handleOpenRate(data.vrs)} />}
                            {/* <Button className="btn-company-contact" type="medium" name="SPECIAL QUOTE" /> */}
                        </div>
                    </Col>
                </Row>
            )}
        </div>
    );
};

CompanyItem.propTypes = {
    data: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        count: PropTypes.number,
        data: PropTypes.oneOfType([
            PropTypes.array,
            PropTypes.shape({
                franchises: PropTypes.array,
                has_blog: PropTypes.string,
                has_connections: PropTypes.string,
                has_hcm: PropTypes.string,
                has_library: PropTypes.string,
                has_products: PropTypes.string,
                profile: PropTypes.oneOfType([
                    PropTypes.array,
                    PropTypes.shape({
                        about: PropTypes.string,
                        avatar: PropTypes.string,
                        cover: PropTypes.string,
                    }),
                ]),
            }),
        ]),
    }),
    type: PropTypes.oneOf(['simple', 'detail']),
};

CompanyItem.defaultProps = {
    data: {
        id: 0,
        name: '',
        count: 0,
        data: {
            has_blog: 'false',
            has_connections: 'false',
            has_hcm: 'false',
            has_library: 'standard',
            has_products: 'false',
            profile: null,
        },
    },
    type: 'simple',
};

export default memo(CompanyItem);
